/**
 * view_item_list tracking. Any element tagged `data-analytics-list` (with
 * `data-analytics-list-name` / `data-analytics-list-id`) is observed; when it is
 * in the viewport we collect its product-card item JSON and fire one
 * view_item_list event for that set of cards.
 *
 * A list fires again when its card set changes while in view — i.e. collection
 * filtering, pagination, or AJAX load-more, which reuse the same container
 * (GA4 treats each of those as a new list view). Identical sets are deduped via
 * a signature so scrolling in and out does not re-fire. Cards injected
 * asynchronously (product recommendations via the Section Rendering API) and
 * containers added to the DOM after load are handled too.
 */
import { track } from '@theme/analytics';

/** @type {WeakSet<Element>} containers already wired up */
const registered = new WeakSet();
/** @type {WeakSet<Element>} containers currently in the viewport */
const visible = new WeakSet();
/** @type {WeakSet<Element>} containers with a fire already scheduled this frame */
const pending = new WeakSet();
/** @type {WeakMap<Element, string>} last card-set signature fired per container */
const lastSignature = new WeakMap();

/**
 * Collect card items within a list container.
 * @param {Element} container
 */
function collect(container) {
  const listName = container.getAttribute('data-analytics-list-name') || '';
  const listId = container.getAttribute('data-analytics-list-id') || '';
  const nodes = container.querySelectorAll('script.analytics-item[data-analytics-context="card"]');
  const items = [];
  nodes.forEach((node, i) => {
    if (!node.textContent) return;
    try {
      const item = JSON.parse(node.textContent);
      item.index = i + 1;
      if (listName) item.item_list_name = listName;
      if (listId) item.item_list_id = listId;
      items.push(item);
    } catch (error) {
      /* skip malformed */
    }
  });
  return { items, listName, listId };
}

/** A stable fingerprint of the current card set (ids + positions). */
function signatureOf(items) {
  return items.map((it) => `${it.item_id || ''}:${it.index}`).join('|');
}

/**
 * Fire view_item_list for a container's current cards, unless that exact set was
 * already reported.
 * @param {Element} container
 */
function fireList(container) {
  const { items, listName, listId } = collect(container);
  if (!items.length) return;

  const signature = signatureOf(items);
  if (lastSignature.get(container) === signature) return; // same set — already sent
  lastSignature.set(container, signature);

  const ecommerce = { items: items.slice(0, 200) };
  if (listName) ecommerce.item_list_name = listName;
  if (listId) ecommerce.item_list_id = listId;
  track('view_item_list', { ecommerce });
}

/** Coalesce bursts of mutations/intersections into one fire per frame. */
function scheduleFire(container) {
  if (pending.has(container)) return;
  pending.add(container);
  window.requestAnimationFrame(() => {
    pending.delete(container);
    if (visible.has(container)) fireList(container);
  });
}

function init() {
  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                visible.add(entry.target);
                scheduleFire(entry.target);
              } else {
                visible.delete(entry.target);
              }
            }
          },
          { threshold: 0.01 }
        )
      : null;

  /** @param {Element} el */
  function register(el) {
    if (registered.has(el)) return;
    registered.add(el);

    if (io) {
      io.observe(el);
    } else {
      // No IntersectionObserver: treat as always in view.
      visible.add(el);
      scheduleFire(el);
    }

    // Re-fire when the card set changes while in view (filter / pagination /
    // async recommendations). The signature check dedupes no-op mutations.
    const mo = new MutationObserver(() => scheduleFire(el));
    mo.observe(el, { childList: true, subtree: true });
  }

  document.querySelectorAll('[data-analytics-list]').forEach(register);

  // Catch list containers added to the DOM after load (Section Rendering API).
  const domObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const el = /** @type {Element} */ (node);
        if (el.matches && el.matches('[data-analytics-list]')) register(el);
        if (el.querySelectorAll) el.querySelectorAll('[data-analytics-list]').forEach(register);
      });
    }
  });
  domObserver.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
